import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BagContents = ({ 
  description = '', 
  estimatedItems = [], 
  dietaryInfo = {},
  allergens = [],
  qualityStandards = []
}) => {
  const [showAllItems, setShowAllItems] = useState(false);
  
  const {
    isVegetarian = false,
    isVegan = false,
    isGlutenFree = false,
    isHalal = false,
    isKosher = false
  } = dietaryInfo;

  const displayedItems = showAllItems ? estimatedItems : estimatedItems?.slice(0, 4);

  const getDietaryIcon = (type) => {
    switch (type) {
      case 'vegetarian':
        return 'Leaf';
      case 'vegan':
        return 'Sprout';
      case 'gluten-free':
        return 'Wheat';
      case 'halal':
        return 'Moon';
      case 'kosher':
        return 'Star';
      default:
        return 'Check';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
        Contenido Estimado
      </h3>
      {/* Description */}
      {description && (
        <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
          {description}
        </p>
      )}
      {/* Estimated Items */}
      {estimatedItems?.length > 0 && (
        <div className="space-y-3 mb-4">
          <h4 className="font-medium text-foreground text-sm">Productos incluidos:</h4>
          <div className="space-y-2">
            {displayedItems?.map((item, index) => (
              <div key={index} className="flex items-center space-x-3 p-2 bg-muted/50 rounded-md">
                <Icon name="Package2" size={16} color="var(--color-primary)" />
                <div className="flex-1">
                  <span className="text-sm font-medium text-foreground">{item?.name}</span>
                  {item?.quantity && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({item?.quantity})
                    </span>
                  )}
                </div>
                {item?.originalPrice && (
                  <span className="text-xs text-muted-foreground">
                    ${item?.originalPrice?.toFixed(2)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {estimatedItems?.length > 4 && (
            <button
              onClick={() => setShowAllItems(!showAllItems)}
              className="text-sm text-primary hover:text-primary/80 font-medium transition-smooth"
            >
              {showAllItems ? 'Ver menos' : `Ver ${estimatedItems?.length - 4} productos más`}
            </button>
          )}
        </div>
      )}
      {/* Dietary Information */}
      <div className="space-y-4">
        {(isVegetarian || isVegan || isGlutenFree || isHalal || isKosher) && (
          <div>
            <h4 className="font-medium text-foreground text-sm mb-2">Información dietética:</h4>
            <div className="flex flex-wrap gap-2">
              {isVegetarian && (
                <div className="flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-md">
                  <Icon name={getDietaryIcon('vegetarian')} size={14} />
                  <span className="text-xs font-medium">Vegetariano</span>
                </div>
              )}
              {isVegan && (
                <div className="flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-md">
                  <Icon name={getDietaryIcon('vegan')} size={14} />
                  <span className="text-xs font-medium">Vegano</span>
                </div>
              )}
              {isGlutenFree && (
                <div className="flex items-center space-x-1 bg-warning/10 text-warning px-2 py-1 rounded-md">
                  <Icon name={getDietaryIcon('gluten-free')} size={14} />
                  <span className="text-xs font-medium">Sin Gluten</span>
                </div>
              )}
              {isHalal && (
                <div className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-md">
                  <Icon name={getDietaryIcon('halal')} size={14} />
                  <span className="text-xs font-medium">Halal</span>
                </div>
              )}
              {isKosher && (
                <div className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-md">
                  <Icon name={getDietaryIcon('kosher')} size={14} />
                  <span className="text-xs font-medium">Kosher</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Allergen Warnings */}
        {allergens?.length > 0 && (
          <div>
            <h4 className="font-medium text-foreground text-sm mb-2">Alérgenos:</h4>
            <div className="bg-warning/10 border border-warning/20 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5" />
                <div>
                  <p className="text-sm text-warning font-medium mb-1">
                    Contiene o puede contener:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {allergens?.map((allergen, index) => (
                      <span key={index} className="text-xs bg-warning/20 text-warning px-2 py-1 rounded">
                        {allergen}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quality Standards */}
        {qualityStandards?.length > 0 && (
          <div>
            <h4 className="font-medium text-foreground text-sm mb-2">Estándares de calidad:</h4>
            <div className="space-y-2">
              {qualityStandards?.map((standard, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                  <span className="text-sm text-muted-foreground">{standard}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BagContents;